import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from './Layout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { 
  FileText, 
  Star, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Loader2,
  ExternalLink,
  MessageSquare
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewMetricsDashboard() {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [amendments, setAmendments] = useState([]);
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    fetchData();
  }, [periodDays]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metricsRes, summaryRes, amendmentsRes] = await Promise.all([
        axios.get(`${API}/review-workflow/metrics?period_days=${periodDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/review-workflow/metrics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/review-workflow/amendments?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMetrics(metricsRes.data);
      setSummary(summaryRes.data);
      setAmendments(amendmentsRes.data.amendments || []);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleAmendmentAction = async (amendmentId, action) => {
    try {
      await axios.put(`${API}/review-workflow/amendments/${amendmentId}`, {
        status: action
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Amendment ${action}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update amendment');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'Draft': { variant: 'secondary', icon: FileText },
      'Review Requested': { variant: 'outline', icon: Star },
      'Amendments Requested': { variant: 'warning', icon: AlertCircle },
      'Approved - Pending Final': { variant: 'default', icon: CheckCircle },
      'Final Issued': { variant: 'success', icon: CheckCircle },
    };
    const config = configs[status] || configs['Draft'];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="review-metrics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Review Metrics</h2>
          <p className="text-slate-600">Track client review engagement and conversion</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodDays.toString()} onValueChange={(v) => setPeriodDays(parseInt(v))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Draft Reports</p>
              <p className="text-2xl font-bold">{metrics?.draft_reports_generated || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Reviews Clicked</p>
              <p className="text-2xl font-bold">{metrics?.reviews_clicked || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Maybe Later</p>
              <p className="text-2xl font-bold">{metrics?.maybe_later_count || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Amendments</p>
              <p className="text-2xl font-bold">{metrics?.amendments_requested || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Finals Issued</p>
              <p className="text-2xl font-bold">{metrics?.final_reports_issued || 0}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Conversion Rate</p>
              <p className="text-2xl font-bold">{metrics?.conversion_rate || 0}%</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Rates Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" />
              Review Click Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold">{metrics?.review_click_rate || 0}%</span>
              <span className="text-sm text-slate-500 mb-1">of drafts sent</span>
            </div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full transition-all"
                style={{ width: `${Math.min(metrics?.review_click_rate || 0, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              Draft to Final Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold">{metrics?.conversion_rate || 0}%</span>
              <span className="text-sm text-slate-500 mb-1">conversion</span>
            </div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${Math.min(metrics?.conversion_rate || 0, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-orange-500" />
              Amendment Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold">{metrics?.amendment_rate || 0}%</span>
              <span className="text-sm text-slate-500 mb-1">require changes</span>
            </div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-orange-500 rounded-full transition-all"
                style={{ width: `${Math.min(metrics?.amendment_rate || 0, 100)}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Amendments */}
      {amendments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Pending Amendments ({amendments.length})
            </CardTitle>
            <CardDescription>Client amendment requests requiring attention</CardDescription>
          </CardHeader>
          <CardContent>
            <Table data-testid="amendments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Report ID</TableHead>
                  <TableHead>Comments</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {amendments.map((amendment) => (
                  <TableRow key={amendment.id}>
                    <TableCell className="font-medium">{amendment.client_name}</TableCell>
                    <TableCell className="font-mono text-sm">{amendment.report_id?.slice(0, 8)}</TableCell>
                    <TableCell className="max-w-[300px] truncate">{amendment.amendment_comments}</TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(amendment.requested_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAmendmentAction(amendment.id, 'in_progress')}
                        >
                          Start
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleAmendmentAction(amendment.id, 'resolved')}
                        >
                          Resolve
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Monthly Chart Placeholder */}
      {metrics?.monthly_data?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Monthly Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-2">
              {metrics.monthly_data.map((month, i) => (
                <div key={i} className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">{month.month}</p>
                  <p className="text-lg font-bold text-blue-600">{month.drafts}</p>
                  <p className="text-xs text-slate-500">drafts</p>
                  <p className="text-sm font-semibold text-green-600">{month.finals}</p>
                  <p className="text-xs text-slate-500">finals</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      {summary?.recent_drafts?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Draft Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.recent_drafts.map((draft, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{draft.client_name || 'N/A'}</p>
                    <p className="text-sm text-slate-500">ID: {draft.report_id?.slice(0, 8)}</p>
                  </div>
                  {getStatusBadge(draft.review_status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
